import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ContentChild,
  DoCheck,
  Input,
  IterableDiffer,
  IterableDiffers,
  OnDestroy,
  OnInit,
  TemplateRef,
  inject,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconButton } from '@angular/material/button';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { Observable, of, Subject } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'searchable',
  templateUrl: './searchable.component.html',
  styleUrls: ['./searchable.component.scss'],
  imports: [MatFormField, MatLabel, MatInput, FormsModule, ReactiveFormsModule, MatIconButton, MatSuffix, MatIcon, NgTemplateOutlet],
})
export class SearchableComponent<T> implements OnInit, OnDestroy, DoCheck {
  private cdr = inject(ChangeDetectorRef);
  private differs = inject(IterableDiffers);

  private readonly $ngDestroyed = new Subject<void>();
  private dataDiffer: IterableDiffer<T>;

  @Input({ required: true }) data: T[] = [];
  @Input({ required: true }) filterCallback!: (data: T[], searchTerm: string) => T[];
  @Input() label = 'Search';

  searchControl = new FormControl('', { nonNullable: true });
  filteredData$: Observable<T[]> = of([]);
  prefilteredCount: number = 0;
  postfilteredCount: number = 0;
  isFilterApplied: boolean = false;

  @ContentChild(TemplateRef) contentTemplate!: TemplateRef<unknown>;

  constructor() {
    this.dataDiffer = this.differs.find([]).create();
  }

  /**
   * Initializes the component and sets up the filtered data observable.
   *
   * @remarks
   * This method is called once the component is initialized. It sets up an observable
   * that listens to changes in the search control's value and filters the input data
   * accordingly. It also initializes the pre-filtered and post-filtered counts.
   *
   * @returns void
   */
  ngOnInit() {
    // initially the counts are same until a filter is applied
    this.prefilteredCount = this.postfilteredCount = this.data.length;
    // setup the observable to filter the data
    this.filteredData$ = this.searchControl.valueChanges.pipe(
      takeUntil(this.$ngDestroyed),
      startWith(''),
      map((searchTerm) => {
        this.prefilteredCount = this.data.length;
        const filteredData = this.filterCallback(this.data, searchTerm);
        this.postfilteredCount = filteredData.length;
        this.isFilterApplied = searchTerm.length > 0;
        return filteredData;
      })
    );
  }

  /**
   * Cleans up resources when the component is destroyed.
   *
   * @remarks
   * This method is called when the component is being destroyed.
   * It emits a value to the `$ngDestroyed` subject to notify any
   * subscribers to complete their subscriptions and then completes
   * the subject itself to free up resources.
   *
   * @returns void
   */
  ngOnDestroy(): void {
    this.$ngDestroyed.next();
    this.$ngDestroyed.complete();
  }

  /**
   * Detects changes in the input data and refreshes the filtered results if any changes are found.
   *
   * @remarks
   * This method uses Angular's `IterableDiffer` to check for changes in the `data` input array.
   * If changes are detected, it calls the `refresh` method to reapply the current search term
   * and update the filtered results accordingly.
   */
  ngDoCheck() {
    const changes = this.dataDiffer.diff(this.data);
    if (changes) {
      this.refresh();
    }
  }

  /**
   * Clears the current search term in the search control.
   *
   * @remarks
   * This method resets the search input to an empty string and triggers change detection
   * to update the filtered data accordingly.
   */
  clear() {
    this.searchControl.setValue('');
    this.cdr.detectChanges();
  }

  /**
   * Triggers a refresh of the filtered data by reapplying the current search term.
   * This is useful when the underlying data changes and you want to ensure
   * that the filtered results are up-to-date.
   */
  refresh() {
    // trigger a refresh of the data
    const currentValue = this.searchControl.value;
    this.searchControl.setValue(currentValue);
  }
}
